/**
 * The plain-English NOTAM decoder of the identify popup (sign-off 04-009; docs/specs/map.md §14.7). It turns a NAIPS
 * NOTAM (`naips-briefing.ts`) into words a crew member or project manager reads at a glance: abbreviations in words,
 * heights with their reference, positions in degrees, minutes and seconds, the validity, schedule and activation
 * periods in the project's local time. The NOTAM as NAIPS printed it is kept beside it (`raw`), and the identify rows
 * still give the text and the UTC times exactly as sent.
 *
 * Diurnal source: `aircraft-map.js` `NOTAM_QCODE_MAP` 12387, `NOTAM_ABBREV_MAP` 12473, `_notamDecodeQCode` 12507,
 * `_notamExpandAbbreviations` 12520 and the popup `_showNotamDetail` 12533 (inventory 04 §3.6).
 *
 * Changed from the tool:
 * - The tool appended a word after each abbreviation ("U/S (unserviceable)"); Teslyn writes the words in place and
 *   lower-cases plain English words, so a line reads as a sentence. Words it does not know (place names, waypoints,
 *   airways, designators such as R156 or (RA2)) stay as NAIPS wrote them. A single word in brackets is kept, since
 *   NAIPS puts a definition there: "PILOT ACT LGT (PAL) U/S".
 * - The tool's tables had duplicates where the later entry silently won (inventory 04 §3.6): `QMRLC` was both "ILS
 *   unserviceable" and "Runway closed", `TWR`, `DME` and `NM` twice. Here every abbreviation has one meaning, and the
 *   Q-code is read as ICAO's subject (letters 2 and 3) and condition (letters 4 and 5) instead of a list of whole codes.
 * - Times: the tool showed UTC only. Teslyn gives the project's local time (its UTC offset, as the rest of the map)
 *   and keeps the UTC times NAIPS wrote in the rows below and in each schedule line.
 *
 * NAIPS domestic NOTAMs carry no Q-line, so `qCode` is null for every NOTAM in the genuine captures; the Q-code words
 * are used when a NOTAM's text has an ICAO "Q)" line.
 */
import type { NaipsNotam, NotamHeight } from './naips-briefing.js';
import { formatOffset, utcToLocalMs } from './time-model.js';

// ------------------------------------------------------------------------------------------------ the tables

/**
 * Abbreviations in NOTAM text and their words (AIP Australia GEN 2.2 and ICAO Doc 8400 meanings). The tool's table
 * (am:12473) with its duplicates resolved, and the abbreviations of the genuine NAIPS briefings of 19 Oct 2026.
 * Two-letter codes that are also navaid or place codes in routes (PH, DU, CB, TW) are left out on purpose.
 */
export const NOTAM_ABBREVIATIONS: Readonly<Record<string, string>> = {
  'A/G': 'air-ground',
  ABV: 'above',
  ACFT: 'aircraft',
  ACT: 'activated',
  AD: 'aerodrome',
  ADJ: 'adjacent',
  AGL: 'above ground level',
  AH: 'after hours',
  ALT: 'altitude',
  ALTN: 'alternate',
  AMSL: 'above mean sea level',
  APCH: 'approach',
  APN: 'apron',
  APP: 'approach',
  APRX: 'approximately',
  ARP: 'aerodrome reference point',
  ARR: 'arrival',
  ASDA: 'accelerate-stop distance available (ASDA)',
  ATC: 'air traffic control',
  AUTH: 'authorised',
  AVBL: 'available',
  AWIS: 'automatic weather information service (AWIS)',
  BCST: 'broadcast',
  BDRY: 'boundary',
  BLDG: 'building',
  BLW: 'below',
  BRG: 'bearing',
  BTN: 'between',
  CAT: 'category',
  CEN: 'centre',
  CHG: 'change',
  CLR: 'clear',
  CLSD: 'closed',
  COM: 'communications',
  COMMS: 'communications',
  CONST: 'construction',
  CTA: 'control area',
  CTAF: 'the common traffic advisory frequency (CTAF)',
  CTC: 'contact',
  CTR: 'control zone',
  CWY: 'clearway',
  DCT: 'direct',
  DEG: 'degrees',
  DEP: 'departure',
  DLY: 'daily',
  DME: 'distance measuring equipment (DME)',
  DUE: 'due to',
  ELEV: 'elevation',
  EMERG: 'emergency',
  ENR: 'en route',
  EQPT: 'equipment',
  EST: 'estimated',
  ETA: 'estimated time of arrival',
  EXC: 'except',
  EXP: 'expect',
  FAC: 'facility',
  FL: 'flight level',
  FLT: 'flight',
  FLW: 'following',
  FM: 'from',
  FREQ: 'frequency',
  FT: 'ft',
  GA: 'general aviation',
  GEN: 'general',
  GND: 'ground',
  H24: '24 hours',
  HGT: 'height',
  HJ: 'sunrise to sunset',
  HLS: 'helicopter landing site',
  HN: 'sunset to sunrise',
  HR: 'hours',
  HRS: 'hours',
  INFO: 'information',
  INTRP: 'interruption',
  INTST: 'intensity',
  KG: 'kg',
  KGS: 'kg',
  KT: 'knots',
  LDA: 'landing distance available (LDA)',
  LDG: 'landing',
  LGT: 'lighting',
  LOC: 'localiser',
  LSALT: 'lowest safe altitude (LSALT)',
  MAG: 'magnetic',
  MAINT: 'maintenance',
  MAX: 'maximum',
  MET: 'meteorological',
  MIL: 'military',
  MIN: 'minutes',
  MINS: 'minutes',
  MNM: 'minimum',
  MNT: 'monitor',
  'N/AVBL': 'not available',
  NDB: 'non-directional beacon (NDB)',
  NM: 'nautical miles',
  NR: 'number',
  OBST: 'obstacle',
  OBSTS: 'obstacles',
  OLS: 'the obstacle limitation surface (OLS)',
  OPR: 'operator',
  OPS: 'operations',
  PAL: 'pilot-activated lighting',
  PAPI: 'precision approach path indicator (PAPI)',
  PERM: 'permanent',
  PN: 'prior notice',
  PPR: 'prior permission required',
  PRKG: 'parking',
  PROC: 'procedure',
  PSN: 'position',
  PWR: 'power',
  RAD: 'radius',
  RCL: 'runway centreline',
  RDO: 'radio',
  REF: 'refer to',
  REP: 'reporting',
  RESA: 'runway end safety area (RESA)',
  RPA: 'remotely piloted aircraft',
  RPT: 'report',
  RQ: 'request',
  RQMNTS: 'requirements',
  RSTD: 'restricted',
  RTE: 'route',
  RWY: 'runway',
  RWYS: 'runways',
  SDBY: 'standby',
  SEC: 'seconds',
  SER: 'service',
  SFC: 'surface',
  SKED: 'scheduled',
  STD: 'standard',
  SUBJ: 'subject to',
  SUP: 'supplement',
  SVC: 'service',
  SVCS: 'services',
  SVY: 'survey',
  TEL: 'telephone',
  TEMPO: 'temporary',
  TFC: 'traffic',
  THR: 'threshold',
  TIL: 'until',
  TKOF: 'take-off',
  TNS: 'the transitional surface',
  TODA: 'take-off distance available (TODA)',
  TORA: 'take-off run available (TORA)',
  TWR: 'tower',
  TWY: 'taxiway',
  TWYS: 'taxiways',
  'U/S': 'unserviceable',
  UA: 'unmanned aircraft',
  UNL: 'unlimited',
  VCY: 'vicinity',
  VOR: 'VHF omnidirectional range (VOR)',
  WDI: 'wind direction indicator (WDI)',
  WI: 'within',
  WID: 'width',
  WIP: 'work in progress',
  WPT: 'waypoint',
  WX: 'weather',
};

/**
 * Plain English words NAIPS writes in capitals, written lower-case in the decoded text. A word not listed (a place,
 * a waypoint, an airway, a code) stays in capitals as NAIPS wrote it, so a name is never mistaken for a word.
 */
const PLAIN_WORDS = new Set(
  `A ABEAM ACTIVATED ACTIVATION ACTIVITY ADD ADDITIONAL ADVISE ADVISORY AERODROME AERODROMES AFTER AIR AIRBORNE
  AIRCRAFT AIRPORT AIRPORTS AIRSPACE ALL ALONG ALTERNATE ALTITUDE AMENDED AMENDMENTS AN AND ANTENNA ANY APPLICABLE
  APPLY APPROACH APPROVAL APPROVED APRON ARE AREA ARRIVAL AS ASPHALT ASSOCIATED AT AUTHORISATIONS AUTHORISED AUTOMATED
  AVAILABLE AVIATION BACK BASE BATTERY BAY BE BELOW BETWEEN BEYOND BIRD BIRDS BLASTING BLOCK BLOCKS BLUE BOTH BOUNDED BOWSER BRICK
  BRIDGE BUILDING BY CALCULATED CALLSIGN CAN CARD CENTRAL CENTRE CHANGE CHANGES CHART CHARTS CHIEF CIRCLING CIVILIAN
  CLASSIFICATION CLEAR CLEARANCE CLEARED CLIMB CLOSED COMMENCES COMMON COMMUNICATION COMPLETE COMPLY CONDUCT CONFIRM
  CONGESTION CONICAL CONTACT CORRUPT COURSE CRANE CRANES CREW CRITICAL CROSSING DATA DAILY DEACTIVATED DECK DECLARED
  DEFENCE DELAY DEPARTING DEPARTURE DEPARTURES DESIGNATED DETAIL DETAILS DEVIATION DIAGRAM DIMENSIONS DIRECTED
  DIRECTLY DISCONNECT DISPLAY DISTANCE DISTANCES DO DOWN DOWNGRADED DRILL DURING DUTY EAST EDGE EITHER ELEVATION EMAIL END
  ENGINE ENQUIRIES EQUIPMENT ERECTED EXCLUDING EXERCISE EXTERNAL FACILITIES FADED FALSE FAULT FEES FIELD FIRE FIREFIGHTING FIXED
  FLIGHT FLIGHTS FLOW FLY FLYING FOLLOWING FOR FORCE FORWARD FROM FUEL FURTHER GENERAL GEOPHYSICAL GRADED GRADIENT
  GREEN GRID GROUND HANDBOOK HANDLING HAZARD HIGH HORIZONTAL HOSPITAL HOURS HOUSE ILLUMINATED IN INCREASE INCREASED
  INDICATIONS INDICATOR INFORMATION INFRINGES INFRINGING INNER INSTRUMENT INTERVALS INTO IS JET KEY LANDING LANE LAUNCH LEAST
  LED LEISURE LEVEL LIGHT LIGHTING LIGHTNING LIGHTS LINE LINK LIT LOAD LOADING LOCAL LOCATION LOCATIONS LOW LOWER
  LOWEST MANAGEMENT MANAGER MANUAL MANUALLY MARSHALLED APPOINTED MARKED MARKINGS MAST MAY MEMBER METHOD MINIMA MINIMUM MONITOR MOVEMENT
  MULTI MULTICOPTER MULTIPLE MUST NATIONAL NAVAL NAVIGATION NEW NIL NO NON NORTH NORTHERN NOT NOTIFIED OBSTACLE
  OBSTACLES OBTAIN OCCUR OF OFF OFFICER ON ONLINE ONLY ONTO OPERATIONS OPERATOR OPERATORS OPTIONS OR OTHER OUT OUTER
  OUTSIDE PARK PATH PENETRATED PENETRATION PERCENT PERFORMANCE PERFORMED PHONE PILOT PILOTED PLACE PLAN PLANNING
  POLES POSSIBLE POST POWER PRIMARY PRINT PRIOR PRIORITY PROCEDURES PROGRAM PROJECTORS PROVIDED PUBLICATIONS PUSH
  RADIO RADIUS READ RED REFER REFERS REGULATIONS RELIABLE REMAIN REMAINING REMARKS REMOTELY REMOVE REPLACEMENT REPORTING
  REQUIRED RESCUE RIDGE ROAD ROOF ROTOR ROUGH ROUTE SAFE SAFETY SCHEDULED SEALED SEALING SECTION SECTOR SECTORS SEPARATE
  SERVICES SIDE SIDES SIGN SIGNAL SITE SLOPE SOLAR SOUTH STAFF STAGE STANDARD START STATION STATUS STEADY STOP
  STRAINER SUPERVISOR SUPPLEMENT SUPPLEMENTARY SURVEY SWITCHOVER SYSTEM TAKE TAKEOFF TARGET TAXIING TERMINAL TERRAIN TEST THE
  TIME TIMES TO TOW TOWER TOWERS TRAFFIC TRANSITIONAL TREES TRIGGER TRUCK TURBINE TYPE UNDERSLUNG UNLIT UNRELIABLE UNSEALED UP USE UTILISED VACATE
  VERIFY VIA VICINITY WATER WEBSITE WEST WESTERN WHILST WIDTH WILL WIND WING WITH WITHIN WITHOUT WORKERS WORKING
  WORKS`.split(/\s+/),
);

/** Publications NAIPS names in short forms, said in full once. */
const PUBLICATIONS: ReadonlyArray<[RegExp, string]> = [
  [
    /\b(?:EN ROUTE|ENR) (?:SUPPLEMENT|SUP) AUSTRALIA(?: \(ERSA\))?/g,
    'the En Route Supplement Australia (ERSA)',
  ],
  [/\bDESIGNATED AIRSPACE HANDBOOK(?: \(DAH\))?/g, 'the Designated Airspace Handbook (DAH)'],
  [
    /\b(?:AIP )?(?:DEP|DEPARTURE) AND (?:APCH|APPROACH)(?: (?:PROC|PROCEDURES))? \(DAP\)/g,
    'the Departure and Approach Procedures (DAP)',
  ],
  // two-word phrases whose second word the token table would otherwise double ("subject to to")
  [/\bSUBJ TO\b/g, 'SUBJ'],
  [/\bDUE TO\b/g, 'DUE'],
  [/\bBRG\(T\)/g, 'bearing (true)'],
  [/\bLAT\s+LONG\b/g, 'latitude longitude'],
  // "TEL:[removed]": the capture's removal marker glued to its label
  [/:\[removed\]/g, ': [removed]'],
];

/** ICAO NOTAM subjects (Q-code letters 2 and 3, ICAO Doc 8126). */
const Q_SUBJECTS: Readonly<Record<string, string>> = {
  AA: 'Minimum altitude',
  AC: 'Control zone (CTR)',
  AE: 'Control area (CTA)',
  AF: 'Flight information region (FIR)',
  AN: 'Area navigation route',
  AP: 'Reporting point',
  AR: 'ATS route',
  AT: 'Terminal control area (TMA)',
  AZ: 'Aerodrome traffic zone',
  CA: 'Air-ground facility',
  CS: 'Secondary surveillance radar',
  FA: 'Aerodrome',
  FF: 'Fire fighting and rescue',
  FH: 'Helicopter landing area',
  FM: 'Meteorological service',
  FU: 'Fuel availability',
  FW: 'Wind direction indicator',
  GA: 'GNSS aerodrome operations',
  GW: 'GNSS area-wide operations',
  IC: 'Instrument landing system (ILS)',
  ID: 'DME of the ILS',
  IG: 'ILS glide path',
  IL: 'ILS localiser',
  IM: 'ILS middle marker',
  IO: 'ILS outer marker',
  LA: 'Approach lighting',
  LB: 'Aerodrome beacon',
  LC: 'Runway centreline lights',
  LE: 'Runway edge lights',
  LG: 'Pilot-activated lighting',
  LH: 'High intensity runway lights',
  LL: 'Low intensity runway lights',
  LM: 'Medium intensity runway lights',
  LP: 'Precision approach path indicator (PAPI)',
  LR: 'All landing area lighting',
  LT: 'Threshold lights',
  LV: 'Visual approach slope indicator system (VASIS)',
  LX: 'Taxiway centreline lights',
  LY: 'Taxiway edge lights',
  MA: 'Movement area',
  MD: 'Declared distances',
  MK: 'Parking area',
  MN: 'Apron',
  MR: 'Runway',
  MS: 'Stopway',
  MT: 'Threshold',
  MW: 'Runway strip',
  MX: 'Taxiway',
  NA: 'All radio navigation facilities',
  NB: 'Non-directional beacon (NDB)',
  ND: 'Distance measuring equipment (DME)',
  NM: 'VOR/DME',
  NN: 'TACAN',
  NV: 'VOR',
  OA: 'Aeronautical information service',
  OB: 'Obstacle',
  OL: 'Obstacle lights',
  PA: 'Standard instrument arrival (STAR)',
  PD: 'Standard instrument departure (SID)',
  PH: 'Holding procedure',
  PI: 'Instrument approach procedure',
  PM: 'Aerodrome operating minima',
  PU: 'Missed approach procedure',
  RA: 'Airspace reservation',
  RD: 'Danger area',
  RM: 'Military operating area',
  RP: 'Prohibited area',
  RR: 'Restricted area',
  RT: 'Temporary restricted area',
  SA: 'Automatic terminal information service (ATIS)',
  SE: 'Flight information service',
  SP: 'Approach control service',
  ST: 'Aerodrome control tower',
  WA: 'Air display',
  WB: 'Aerobatics',
  WC: 'Captive balloon or kite',
  WD: 'Demolition of explosives',
  WE: 'Exercises',
  WF: 'Air refuelling',
  WG: 'Glider flying',
  WH: 'Blasting',
  WJ: 'Banner or target towing',
  WL: 'Free balloon ascent',
  WM: 'Missile, gun or rocket firing',
  WP: 'Parachute jumping',
  WS: 'Burning or blowing gas',
  WU: 'Unmanned aircraft',
  WV: 'Formation flight',
  WY: 'Aerial survey',
  WZ: 'Model flying',
};

/** ICAO NOTAM conditions (Q-code letters 4 and 5); XX is plain language: the text says it. */
const Q_CONDITIONS: Readonly<Record<string, string>> = {
  AC: 'withdrawn for maintenance',
  AD: 'available for daylight operation',
  AH: 'hours of service changed',
  AK: 'resumed normal operation',
  AL: 'operative subject to the published limitations',
  AM: 'military operations only',
  AN: 'available for night operation',
  AO: 'operational',
  AP: 'available, prior permission required',
  AR: 'available on request',
  AS: 'unserviceable',
  AU: 'not available',
  AW: 'completely withdrawn',
  CA: 'activated',
  CC: 'completed',
  CD: 'deactivated',
  CE: 'erected',
  CF: 'frequency changed',
  CG: 'downgraded',
  CH: 'changed',
  CI: 'identification or call sign changed',
  CL: 'realigned',
  CM: 'displaced',
  CN: 'cancelled',
  CO: 'operating',
  CP: 'operating on reduced power',
  CR: 'temporarily replaced',
  CS: 'installed',
  CT: 'on test, do not use',
  HW: 'work in progress',
  HV: 'work completed',
  LC: 'closed',
  LD: 'unsafe',
  LI: 'closed to IFR operations',
  LN: 'closed to all night operations',
  LP: 'prohibited',
  LS: 'subject to interruption',
  LT: 'limited',
  LV: 'closed to VFR operations',
  LW: 'will take place',
};

/** A Q-code in words: "QMRLC" is "Runway: closed"; null when the code or its subject is not known. */
export function qCodeWords(code: string): string | null {
  const c = code.trim().toUpperCase();
  if (!/^Q[A-Z]{4}$/.test(c)) return null;
  const subject = Q_SUBJECTS[c.slice(1, 3)];
  if (!subject) return null;
  const cond = c.slice(3, 5);
  if (cond === 'XX') return subject;
  const words = Q_CONDITIONS[cond];
  return words ? `${subject}: ${words}` : subject;
}

/** The Q-code of an ICAO-format NOTAM ("Q) YBBB/QMRLC/IV/…"), else null (every NAIPS domestic NOTAM). */
export function notamQCode(text: string): string | null {
  const m = /\bQ\)\s*[A-Z]{4}\/(Q[A-Z]{4})\//.exec(text);
  return m ? m[1]! : null;
}

// ------------------------------------------------------------------------------------------------ a line

const thousands = (digits: string): string => {
  const n = Number(digits.replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('en-AU', { maximumFractionDigits: 3 }) : digits;
};

/** ddmmss(.s)S dddmmss(.s)E, or ddmmS dddmmE: degrees, minutes and seconds with the hemisphere. */
function positionWords(line: string): string {
  return line
    .replace(
      /\b(\d{2})(\d{2})(\d{2}(?:\.\d+)?)([NS])\s+(\d{3})(\d{2})(\d{2}(?:\.\d+)?)([EW])\b/g,
      (_m, ld, lm, ls, ns, gd, gm, gs, ew) =>
        `${Number(ld)}°${lm}′${ls}″ ${ns} ${Number(gd)}°${gm}′${gs}″ ${ew}`,
    )
    .replace(
      /\b(\d{2})(\d{2})([NS])\s+(\d{3})(\d{2})([EW])\b/g,
      (_m, ld, lm, ns, gd, gm, ew) => `${Number(ld)}°${lm}′ ${ns} ${Number(gd)}°${gm}′ ${ew}`,
    );
}

/** Numbers glued to their unit: "2244FT", "2.6NM", "15MIN", "1HR", "25KG", "1650M", "15SEC", "FL160". */
function unitWords(line: string): string {
  const plural = (n: string, one: string, many: string): string =>
    `${thousands(n)} ${Number(n.replace(/,/g, '')) === 1 ? one : many}`;
  return line
    .replace(/\bFL(\d{2,3})\b/g, (_m, l) => `flight level ${Number(l)}`)
    .replace(/\b(\d[\d,]*(?:\.\d+)?)FT\b/g, (_m, n) => `${thousands(n)} ft`)
    .replace(/\b(\d+(?:\.\d+)?)NM\b/g, (_m, n) => plural(n, 'nautical mile', 'nautical miles'))
    .replace(/\b(\d+)MINS?\b/g, (_m, n) => plural(n, 'minute', 'minutes'))
    .replace(/\b(\d+)HRS?\b/g, (_m, n) => plural(n, 'hour', 'hours'))
    .replace(/\b(\d+)SEC\b/g, (_m, n) => plural(n, 'second', 'seconds'))
    .replace(/\b(\d+(?:\.\d+)?)KGS?\b/g, (_m, n) => `${n} kg`)
    .replace(/\b(\d+)KT\b/g, (_m, n) => plural(n, 'knot', 'knots'))
    .replace(/\b(\d[\d,]*(?:\.\d+)?)M\b/g, (_m, n) => `${thousands(n)} m`)
    .replace(/\bBRG (\d{3}(?:\.\d+)?)(?: DEG\b)?(?!°)/g, 'BRG $1°')
    .replace(/\bRWY(\d{2}[LRC]?)-/g, 'RWY $1:');
}

const isUpperWord = (t: string): boolean => /^[A-Z][A-Z0-9/-]*$/.test(t) && /[A-Z]/.test(t);

/** One token in words: an abbreviation, a plain word lower-cased, else as written. */
function tokenWords(core: string, first: boolean): string {
  if (first && core === 'AMD') return 'amends';
  if (core === 'AMD') return 'amended';
  const ab = NOTAM_ABBREVIATIONS[core];
  if (ab !== undefined) return ab;
  const parts = core.split(/[-/]/).filter(Boolean);
  if (parts.length > 0 && parts.every((p) => PLAIN_WORDS.has(p))) return core.toLowerCase();
  return core;
}

/** A line of NOTAM text in plain English; numbers, codes and names stay as NAIPS wrote them. */
export function decodeNotamLine(line: string): string {
  let t = line.replace(/\s+/g, ' ').trim();
  if (!t) return '';
  t = t.replace(/\bFM ARP\b/g, 'FM the ARP');
  for (const [re, words] of PUBLICATIONS) t = t.replace(re, words);
  t = positionWords(t);
  t = unitWords(t);
  const toks = t.split(' ');
  // a route ("DCT TEEVE Q13 BTH W386 VIRUR DCT") names navaids with two letters (AD, PH, CB): left as written
  const route = toks.some((x) => x.replace(/[^A-Z]/g, '') === 'DCT');
  const out = toks.map((tok, i) => {
    // a single word in brackets is NAIPS's own definition, "(PAL)", "(RA2)": kept, unless a plain word ("(LIT)")
    const solo = /^\(([A-Z][A-Z0-9/-]*)\)([.,:;]?)$/.exec(tok);
    if (solo) {
      const w = solo[1]!;
      const parts = w.split(/[-/]/).filter(Boolean);
      return parts.every((p) => PLAIN_WORDS.has(p)) ? `(${w.toLowerCase()})${solo[2]}` : tok;
    }
    const m = /^(\(?)(.*?)([).,:;]*)$/.exec(tok)!;
    const core = m[2]!;
    if (!isUpperWord(core)) return tok;
    if (route && core.length === 2) return tok;
    // a single letter (TWY L, 28M L OF RCL) is a name or a side, never an abbreviation; "A" is the article only
    // between words ("OBTAIN A CALCULATED"), never a taxiway ("TWY A BTN") or a list mark ("A.")
    if (core.length === 1) {
      const prev = toks[i - 1] ?? '';
      const next = (toks[i + 1] ?? '').replace(/[^A-Z]/g, '');
      const article =
        tok === 'A' && !/^(TWY|RWY|APN|BAY|GATE|SECTION|STAGE)$/.test(prev) && PLAIN_WORDS.has(next);
      return article ? 'a' : tok;
    }
    return `${m[1]}${tokenWords(core, i === 0)}${m[3]}`;
  });
  const s = out.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ------------------------------------------------------------------------------------------------ times

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_CODES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const p2 = (v: number): string => String(v).padStart(2, '0');

/** "5 Oct 2026 09:37" in the offset given. */
export function localDateTimeText(utcMs: number, offsetH: number): string {
  const d = new Date(utcToLocalMs(utcMs, offsetH));
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}

const zoneText = (offsetH: number): string => `UTC${formatOffset(offsetH)}`;

function inForceText(n: NaipsNotam, offsetH: number): string {
  if (!n.validFrom) return 'Not stated';
  const zone = `local time (${zoneText(offsetH)})`;
  const from = localDateTimeText(Date.parse(n.validFrom), offsetH);
  if (n.permanent) return `From ${from} ${zone}, permanent`;
  if (!n.validTo) return `From ${from} ${zone}`;
  const to = localDateTimeText(Date.parse(n.validTo), offsetH);
  return `${from} to ${to} ${zone}${n.endEstimated ? '; the end is estimated: in force until cancelled or replaced' : ''}`;
}

/** "2610190000 TO 2610190900": a period, year to minute in UTC, in local time ("19 Oct 2026 08:00 to 17:00"). */
function periodText(line: string, offsetH: number): string | null {
  const m = /^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2}) TO (\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(line);
  if (!m) return null;
  const ms = (o: number): number =>
    Date.UTC(2000 + Number(m[o]), Number(m[o + 1]) - 1, Number(m[o + 2]), Number(m[o + 3]), Number(m[o + 4]));
  const a = localDateTimeText(ms(1), offsetH);
  const b = localDateTimeText(ms(6), offsetH);
  const sameDay = a.slice(0, a.lastIndexOf(' ')) === b.slice(0, b.lastIndexOf(' '));
  return `${a} to ${sameDay ? b.slice(b.lastIndexOf(' ') + 1) : b}`;
}

/** "DAILY 2300-1000", "MON - FRI 2300-0900", "TUE-THU 0000-1400": UTC times moved to local, days moved with them. */
function weeklyText(line: string, offsetH: number): string | null {
  const m =
    /^(DAILY|SUN|MON|TUE|WED|THU|FRI|SAT)(?:\s*-\s*(SUN|MON|TUE|WED|THU|FRI|SAT))?\s+(\d{2})(\d{2})-(\d{2})(\d{2})$/.exec(
      line,
    );
  if (!m) return null;
  const startUtc = Number(m[3]) * 60 + Number(m[4]);
  const endUtc = Number(m[5]) * 60 + Number(m[6]);
  const off = Math.round(offsetH * 60);
  const startLocal = startUtc + off;
  const shift = Math.floor(startLocal / 1440);
  const hm = (min: number): string => {
    const v = ((min % 1440) + 1440) % 1440;
    return `${p2(Math.floor(v / 60))}:${p2(v % 60)}`;
  };
  const days = (shiftBy: number): string => {
    if (m[1] === 'DAILY') return 'Daily';
    const a = (DAY_CODES.indexOf(m[1]!) + shiftBy + 7) % 7;
    if (!m[2]) return DAYS[a]!;
    const b = (DAY_CODES.indexOf(m[2]) + shiftBy + 7) % 7;
    return `${DAYS[a]} to ${DAYS[b]}`;
  };
  const utcDays = days(0);
  const utcWords = m[1] === 'DAILY' ? 'daily' : utcDays;
  return `${days(shift)} ${hm(startLocal)} to ${hm(endUtc + off)} (${utcWords} ${hm(startUtc)} to ${hm(endUtc)} UTC as written)`;
}

const SCHEDULE_WORDS: Readonly<Record<string, string>> = {
  HJ: 'Sunrise to sunset (daylight hours)',
  HN: 'Sunset to sunrise (night)',
  H24: '24 hours',
};

function scheduleLines(schedule: string | null, offsetH: number): string[] {
  if (!schedule) return [];
  return schedule
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .map(
      (l) =>
        SCHEDULE_WORDS[l] ??
        periodText(l, offsetH) ??
        weeklyText(l, offsetH) ??
        `${decodeNotamLine(l)} (times in UTC as written)`,
    );
}

// ------------------------------------------------------------------------------------------------ heights

const metres = (ft: number): string => Math.round(ft * 0.3048).toLocaleString('en-AU');

function heightWords(h: NotamHeight): string {
  if (h.reference === 'SFC') return 'the surface';
  if (h.reference === 'UNL' || h.ft === null) return 'unlimited';
  if (h.reference === 'FL')
    return `flight level ${Math.round(h.ft / 100)} (about ${h.ft.toLocaleString('en-AU')} ft)`;
  const ref = h.reference === 'AGL' ? 'above ground level' : 'above mean sea level';
  return `${h.ft.toLocaleString('en-AU')} ft (${metres(h.ft)} m) ${ref}`;
}

function limitsText(n: NaipsNotam): string | null {
  if (n.lower && n.upper) return `From ${heightWords(n.lower)} to ${heightWords(n.upper)}`;
  if (n.upper) return `Up to ${heightWords(n.upper)}`;
  if (n.lower) return `From ${heightWords(n.lower)} up`;
  return null;
}

// ------------------------------------------------------------------------------------------------ the NOTAM

export interface DecodedNotam {
  /** What the NOTAM is about: the Q-code in words when it has one, else its first line in plain English. */
  readonly heading: string;
  /** The ICAO Q-code and its words; null for NAIPS domestic NOTAMs (no Q-line). */
  readonly qCode: { readonly code: string; readonly words: string | null } | null;
  /** Every line of the text in plain English, one for one. */
  readonly lines: readonly string[];
  /** The validity in the project's local time, with the zone and the estimated end in words. */
  readonly inForce: string;
  /** The schedule and activation periods in local time ("Sunrise to sunset", "19 Oct 2026 08:00 to 17:00"). */
  readonly schedule: readonly string[];
  /** The vertical limits in words, feet with metres; null when the NOTAM states none. */
  readonly limits: string | null;
  /** "UTC+8": the zone of every local time. */
  readonly zone: string;
  /** The NOTAM exactly as NAIPS printed it. */
  readonly raw: string;
}

/** A NAIPS NOTAM in plain English, times in the project's UTC offset (`offsetH`). */
export function decodeNotam(n: NaipsNotam, offsetH: number): DecodedNotam {
  const lines = n.text.split('\n').map(decodeNotamLine);
  const code = notamQCode(n.raw);
  const qWords = code ? qCodeWords(code) : null;
  return {
    heading: qWords ?? lines.find(Boolean) ?? `NOTAM ${n.id}`,
    qCode: code ? { code, words: qWords } : null,
    lines,
    inForce: inForceText(n, offsetH),
    schedule: scheduleLines(n.schedule, offsetH),
    limits: limitsText(n),
    zone: zoneText(offsetH),
    raw: n.raw,
  };
}
